import { formatMoney } from '@/components/data-table/columns-helper';

/**
 * Money the browser may state, or the stored minor units when it may not.
 *
 * Two ways `formatMoney` would name a figure nobody enforced, and both fall
 * back to the raw minor units instead. It divides by the exponent of a currency
 * it DEFAULTS to THB, so a JPY brand's 120000 renders as "฿1,200.00" when it is
 * ¥120,000 — a 100x understatement on the very numbers support quotes to a
 * player (BO-05, #521). And it goes through `Number`, so a value past 2^53
 * comes back as a different one.
 *
 * The raw form is labelled as minor units, because "120000" unlabelled reads as
 * an amount of money too.
 */
export function moneyOrMinor(
  minor: string | number | null | undefined,
  currency: string | null | undefined,
  t: (key: string) => string,
): string {
  if (minor === null || minor === undefined || minor === '') return '-';
  const text = String(minor);
  const n = Number(text);
  if (!currency || !Number.isSafeInteger(n)) {
    return `${text} ${t('promotion.withdrawCap.minorUnits')}`;
  }
  return formatMoney(n, { currency });
}

/**
 * What is left of a promotion withdrawal ceiling: `cap - withdrawn`, floored at
 * zero, in exact minor units.
 *
 * BigInt, not `Number`: this is the figure an operator reads before approving a
 * payout, and a ceiling and a running total that each survive 2^53 must not
 * lose their difference on the way to the screen. Returns `null` when either
 * side is not an integer string — the caller shows nothing rather than a
 * guess.
 *
 * Floored because the two halves come from different services: svc-promo's
 * ceiling is scoped to the operator's claims and svc-finance's sum to their
 * agents, so a member holding a claim the reader cannot see can legitimately
 * show more withdrawn than capped. A negative remainder would read as a debt.
 */
export function remainingMinor(
  capMinor: string | null | undefined,
  withdrawnMinor: string | null | undefined,
): string | null {
  if (!capMinor || !withdrawnMinor) return null;
  if (!/^-?\d+$/.test(capMinor) || !/^-?\d+$/.test(withdrawnMinor)) return null;
  const left = BigInt(capMinor) - BigInt(withdrawnMinor);
  return (left > 0n ? left : 0n).toString();
}
